import { liveQuery, type Subscription } from 'dexie';
import { untrack } from 'svelte';
import { identity } from '$core/identity/state.svelte';

/**
 * Cross-tab live query helper (M4.5b).
 *
 * Dexie's liveQuery re-runs its callback when the underlying IndexedDB
 * changes — including changes made by other tabs. We wire it into Svelte 5
 * via `$effect` so the subscription is set up on mount and torn down on
 * unmount.
 *
 * IMPORTANT: this helper does NOT return a $state. The caller holds the
 * reactive value; this function only manages the subscription lifecycle.
 * Returning a $state from a function would break reactivity in Svelte 5.
 *
 * Usage:
 *   let subjects = $state<Subject[]>([]);
 *   liveSync(() => listSubjects(), (v) => { subjects = v; });
 */
export function liveSync<T>(
  query: () => Promise<T>,
  set: (value: T) => void
): void {
  $effect(() => {
    // Read the active userId reactively: when identity changes
    // (guest → authenticated, account switch), we tear down the old
    // liveQuery subscription and re-subscribe with the new filter.
    // Without this, queries like `listSubjects()` silently keep
    // filtering by the old userId because `getActiveUserId()` itself
    // is not reactive.
    void identity.identity.userId;

    const sub: Subscription = liveQuery(query).subscribe({
      next: (value) => {
        untrack(() => set(value));
      },
      error: (err) => {
        console.error('[StudyQuantifier] liveSync error:', err);
      }
    });
    return () => sub.unsubscribe();
  });
}